import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { Icon } from "../Icon";
import { Modal } from "./Controls";
import { DesignDetail, iconBtn } from "../Designeditorparts";

import {
  fetchStudioFormats,
  fetchStudioVersions,
  saveStudioVersion,
  restoreStudioVersion,
} from "../../../features/studio-controller/StudioServices";
import { AppDispatch } from "../../../store";

type DownloadFormat = "png" | "jpg" | "png-clear" | "pdf";
type DownloadPages = "current" | "all";

interface DownloadOptions {
  format: DownloadFormat;
  pages: DownloadPages;
  scale: number;
}

interface DownloadMenuProps {
  pages: number;
  onDownload: (options: DownloadOptions) => void;
}

interface StudioFormat {
  key: string;
  label: string;
  width: number;
  height: number;
}

interface Design {
  id: number | string;
  width: number;
  height: number;
}

interface ResizeTarget {
  key: string;
  label: string;
  width: number;
  height: number;
}

interface CreatedDesign {
  id: number | string;
  label: string;
  width: number;
  height: number;
  thumbnailUrl?: string | null;
}

interface ResizeDialogProps {
  design: Design;
  busy: boolean;
  onClose: () => void;
  onResize: (options: {
    inPlace: boolean;
    targets: ResizeTarget[];
  }) => Promise<CreatedDesign[]>;
  onOpen: (id: number | string) => void;
}

interface Version {
  id: number | string;
  label?: string | null;
  createdAt: number | string | Date;
  thumbnailUrl?: string | null;
  auto?: boolean;
}

interface VersionsDrawerProps {
  design: Design;
  onClose: () => void;
  onRestored: (design: DesignDetail) => void | Promise<void>;
  beforeSave: () => Promise<string>;
  notify: (message: string, type: "good" | "bad" | "info") => void;
}

interface ShortcutsDialogProps {
  onClose: () => void;
}

interface CustomSize {
  on: boolean;
  unit: SizeUnit;
  width: number | string;
  height: number | string;
  dpi: number;
}

const ring =
  "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[rgba(11,122,90,0.45)]";
const DISPLAY = "font-['Bricolage_Grotesque','IBM_Plex_Sans',sans-serif]";
const MONO = "font-['IBM_Plex_Mono',monospace]";

type Tone = "plain" | "accent";
type Size = "md" | "sm";
const TONE: Record<Tone, string> = {
  plain: "border-st-line bg-white text-st-ink hover:enabled:border-[#9fb3ab]",
  accent:
    "border-st-green bg-st-green text-white hover:enabled:border-st-green-dark hover:enabled:bg-st-green-dark",
};
const SIZE: Record<Size, string> = {
  md: "min-h-[40px] rounded-10 px-3.5 py-[9px] text-sm",
  sm: "min-h-[34px] rounded-[9px] px-[11px] py-[7px] text-[13px]",
};
const btn = (tone: Tone = "plain", size: Size = "md", block = false) =>
  `inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap border font-semibold leading-none disabled:cursor-not-allowed disabled:opacity-50 ${ring} ${SIZE[size]} ${TONE[tone]}${block ? " w-full" : ""}`;

const HINT = "text-[13px] leading-normal text-st-muted";
const HINT_TOP = "mb-3 text-[13px] leading-normal text-st-muted";
const FINEPRINT = "mt-2 text-xs leading-[1.45] text-st-muted";
const CP_H =
  "mb-0.5 mt-1.5 flex items-center gap-[5px] text-xs font-bold text-st-muted first:mt-0";
const INPUT = `w-full resize-y rounded-[9px] border border-st-line bg-white px-[11px] py-[9px] text-sm text-st-ink ${ring}`;
const SKEL =
  "block animate-st-shimmer rounded-10 bg-[linear-gradient(90deg,#edf2ef_0%,#f6f9f7_50%,#edf2ef_100%)] bg-[length:200%_100%]";
const SEG =
  "flex w-full gap-0.5 rounded-10 border border-st-line bg-st-paper p-[3px]";
const SEG_BTN =
  "inline-grid h-[30px] min-w-8 flex-1 cursor-pointer place-items-center rounded-[7px] border-0 bg-transparent px-2 text-[13px] font-semibold text-st-muted aria-pressed:bg-white aria-pressed:text-st-ink aria-pressed:shadow-[0_1px_3px_rgba(16,35,30,0.15)]";
const MODAL_HEAD = "flex items-center justify-between px-[18px] pb-2 pt-4";
const CARD_SOFT = "grid gap-2 rounded-xl bg-st-paper p-3";
const LIST_ROW = "flex items-center gap-3 rounded-xl border border-st-line p-2";

// DOWNLOAD

const FORMATS: Array<[DownloadFormat, string, string]> = [
  ["png", "PNG", "Sharpest quality, best for text and logos"],
  ["jpg", "JPG", "Smaller file, best for WhatsApp and photos"],
  [
    "png-clear",
    "PNG, transparent",
    "No background colour (for logos and stickers)",
  ],
  ["pdf", "PDF", "For printing and sharing documents"],
];

type SizeUnit = "px" | "in" | "cm" | "mm";

const DEFAULT_DPI = 300;

function toPx(value: number, unit: SizeUnit, dpi: number): number {
  if (!Number.isFinite(value)) return 0;
  if (unit === "px") return Math.round(value);
  const inches =
    unit === "in" ? value : unit === "cm" ? value / 2.54 : value / 25.4;
  return Math.round(inches * dpi);
}

function fromPx(px: number, unit: SizeUnit, dpi: number): string {
  if (unit === "px") return String(Math.round(px));
  const inches = px / dpi;
  const value =
    unit === "in" ? inches : unit === "cm" ? inches * 2.54 : inches * 25.4;
  return (Math.round(value * 100) / 100).toString(); // 2 decimal places
}

const UNIT_LABEL: Record<SizeUnit, string> = {
  px: "px",
  in: "in",
  cm: "cm",
  mm: "mm",
};

export function DownloadMenu({ pages, onDownload }: DownloadMenuProps) {
  const [format, setFormat] = useState<DownloadFormat>("png");
  const [which, setWhich] = useState<DownloadPages>("current");
  const [scale, setScale] = useState<number>(1);

  return (
    <div
      className="absolute right-0 top-[calc(100%+8px)] z-30 w-[320px] rounded-[14px] bg-white p-3 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.4),0_0_0_1px_rgba(16,35,30,0.06)] max-[820px]:right-2 max-[820px]:top-[104px]"
      role="dialog"
      aria-label="Download"
    >
      <p className={CP_H}>File type</p>

      <div className="grid gap-1">
        {FORMATS.map(([key, label, hint]) => (
          <label
            key={key}
            className={`flex cursor-pointer items-start gap-2.5 rounded-10 border px-2.5 py-2 ${
              format === key
                ? "border-st-green bg-st-green-soft"
                : "border-transparent"
            }`}
          >
            <input
              className="mt-[3px] accent-st-green"
              type="radio"
              name="dl-format"
              checked={format === key}
              onChange={() => setFormat(key)}
            />

            <span>
              <strong className="block text-st-ink text-sm">{label}</strong>
              <small className="mt-0.5 block text-xs text-st-muted">
                {hint}
              </small>
            </span>
          </label>
        ))}
      </div>

      <p className={CP_H}>Size</p>

      <div className={SEG}>
        <button
          className={SEG_BTN}
          aria-pressed={scale === 1}
          onClick={() => setScale(1)}
        >
          1× (normal)
        </button>

        <button
          className={SEG_BTN}
          aria-pressed={scale === 2}
          onClick={() => setScale(2)}
        >
          2× (extra sharp)
        </button>
      </div>

      {pages > 1 && (
        <>
          <p className={CP_H}>Pages</p>

          <div className={SEG}>
            <button
              className={SEG_BTN}
              aria-pressed={which === "current"}
              onClick={() => setWhich("current")}
            >
              This page
            </button>

            <button
              className={SEG_BTN}
              aria-pressed={which === "all"}
              onClick={() => setWhich("all")}
            >
              All {pages} pages
            </button>
          </div>
        </>
      )}

      <button
        className={`${btn("accent", "md", true)} mt-3`}
        onClick={() =>
          onDownload({
            format,
            pages: which,
            scale,
          })
        }
      >
        <Icon name="download" size={18} />
        Download
      </button>
    </div>
  );
}

// ================================================================
// MAGIC RESIZE
// ================================================================

export function ResizeDialog({
  design,
  busy,
  onClose,
  onResize,
  onOpen,
}: ResizeDialogProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [formats, setFormats] = useState<StudioFormat[]>([]);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const [custom, setCustom] = useState<CustomSize>({
    on: false,
    unit: "px",
    width: design.width,
    height: design.height,
    dpi: DEFAULT_DPI,
  });
  const [created, setCreated] = useState<CreatedDesign[] | null>(null);

  useEffect(() => {
    let mounted = true;

    dispatch(fetchStudioFormats())
      .unwrap()
      .then((result) => {
        if (mounted) {
          setFormats(result || []);
        }
      })
      .catch(() => {
        if (mounted) {
          setFormats([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [dispatch]);

  const targets: ResizeTarget[] = [
    ...formats
      .filter((format) => chosen[format.key])
      .map((format) => ({
        key: format.key,
        label: format.label,
        width: format.width,
        height: format.height,
      })),

    ...(custom.on
      ? [
          {
            key: "CUSTOM",
            label:
              custom.unit === "px"
                ? `${custom.width} × ${custom.height}`
                : `${custom.width}${custom.unit} × ${custom.height}${custom.unit} (${toPx(
                    Number(custom.width) || 0,
                    custom.unit,
                    custom.dpi,
                  )} × ${toPx(Number(custom.height) || 0, custom.unit, custom.dpi)} px)`,
            width: toPx(Number(custom.width) || 0, custom.unit, custom.dpi),
            height: toPx(Number(custom.height) || 0, custom.unit, custom.dpi),
          },
        ]
      : []),
  ].filter(
    (target) =>
      target.width >= 100 &&
      target.height >= 100 &&
      target.width <= 5000 &&
      target.height <= 5000,
  );

  if (created) {
    return (
      <Modal title="Resized copies are ready" onClose={onClose} width={480}>
        <p className={HINT_TOP}>
          Your original design is unchanged. Open a copy to fine-tune it.
        </p>

        <ul className="m-0 grid list-none gap-2 p-0">
          {created.map((designItem) => (
            <li key={designItem.id} className={LIST_ROW}>
              {designItem.thumbnailUrl ? (
                <img
                  className="h-14 w-14 flex-none rounded-lg object-cover"
                  src={designItem.thumbnailUrl}
                  alt=""
                />
              ) : (
                <span className={`${SKEL} h-14 w-14 flex-none rounded-lg`} />
              )}

              <span className="flex-1">
                <strong className="block text-sm">{designItem.label}</strong>
                <small className="text-xs text-st-muted">
                  {designItem.width} × {designItem.height}
                </small>
              </span>

              <button
                className={btn("accent", "sm")}
                onClick={() => onOpen(designItem.id)}
              >
                Open
              </button>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  return (
    <Modal
      title="Magic resize"
      onClose={onClose}
      width={560}
      footer={
        <>
          <span className="mr-auto text-xs leading-[1.45] text-st-muted">
            {targets.length
              ? `${targets.length} size${
                  targets.length > 1 ? "s" : ""
                } selected`
              : "Pick one or more sizes"}
          </span>

          <button
            className={btn("plain", "md")}
            disabled={busy || targets.length !== 1}
            onClick={() =>
              onResize({
                inPlace: true,
                targets,
              })
            }
            title="Changes this design"
          >
            Resize this design
          </button>

          <button
            className={btn("accent", "md")}
            disabled={busy || !targets.length}
            onClick={async () => {
              const result = await onResize({
                inPlace: false,
                targets,
              });

              if (result.length) {
                setCreated(result);
              }
            }}
          >
            {busy ? "Resizing…" : "Copy & resize"}
          </button>
        </>
      }
    >
      <p className={HINT_TOP}>
        Everything on every page is re-laid out for the new size. Now{" "}
        {design.width} × {design.height} px.
      </p>

      <div className="grid gap-1.5">
        {formats.map((format) => {
          const same =
            format.width === design.width && format.height === design.height;
          const on = !!chosen[format.key];

          return (
            <label
              key={format.key}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                on ? "border-st-green bg-st-green-soft" : "border-st-line"
              } ${same ? "cursor-default opacity-[0.55]" : "cursor-pointer"}`}
            >
              <input
                className="h-[17px] w-[17px] accent-st-green"
                type="checkbox"
                disabled={same}
                checked={on}
                onChange={(event) =>
                  setChosen({
                    ...chosen,
                    [format.key]: event.target.checked,
                  })
                }
              />

              <span
                className="max-h-[34px] w-[30px] flex-none rounded-[3px] border-2 border-[#9fb3ab]"
                style={{
                  aspectRatio: `${format.width} / ${format.height}`,
                }}
              />

              <span>
                <strong className="block text-sm">{format.label}</strong>

                <small className="block text-xs text-st-muted">
                  {format.width} × {format.height}
                  {same ? " · current size" : ""}
                </small>
              </span>
            </label>
          );
        })}

        <label
          className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 ${
            custom.on ? "border-st-green bg-st-green-soft" : "border-st-line"
          }`}
        >
          <input
            className="h-[17px] w-[17px] accent-st-green"
            type="checkbox"
            checked={custom.on}
            onChange={(event) =>
              setCustom({
                ...custom,
                on: event.target.checked,
              })
            }
          />

          <span
            className="max-h-[34px] w-[30px] flex-none rounded-[3px] border-2 border-[#9fb3ab]"
            style={{
              aspectRatio: `${custom.width || 1} / ${custom.height || 1}`,
            }}
          />

          <span>
            <strong className="block text-sm">Custom size</strong>

            <span
              className="mt-1 grid gap-1.5 text-[13px] text-st-muted"
              onClick={(e) => e.preventDefault()}
            >
              <span className="flex items-center gap-1.5">
                <input
                  className={`min-h-[32px] w-20 rounded-[9px] border border-st-line bg-white px-2 py-1 text-sm text-st-ink ${ring}`}
                  inputMode="decimal"
                  value={custom.width}
                  aria-label={`Width in ${UNIT_LABEL[custom.unit]}`}
                  onChange={(event) =>
                    setCustom({
                      ...custom,
                      on: true,
                      width:
                        custom.unit === "px"
                          ? event.target.value.replace(/\D/g, "")
                          : event.target.value.replace(/[^0-9.]/g, ""),
                    })
                  }
                />
                ×
                <input
                  className={`min-h-[32px] w-20 rounded-[9px] border border-st-line bg-white px-2 py-1 text-sm text-st-ink ${ring}`}
                  inputMode="decimal"
                  value={custom.height}
                  aria-label={`Height in ${UNIT_LABEL[custom.unit]}`}
                  onChange={(event) =>
                    setCustom({
                      ...custom,
                      on: true,
                      height:
                        custom.unit === "px"
                          ? event.target.value.replace(/\D/g, "")
                          : event.target.value.replace(/[^0-9.]/g, ""),
                    })
                  }
                />
                <select
                  className={`min-h-[32px] cursor-pointer rounded-[9px] border border-st-line bg-white px-1.5 py-1 text-sm text-st-ink ${ring}`}
                  value={custom.unit}
                  aria-label="Unit"
                  onChange={(event) => {
                    const nextUnit = event.target.value as SizeUnit;
                    if (nextUnit === custom.unit) return;
                    // Re-express the current values in the new unit so the number stays meaningful.
                    const curPxW = toPx(
                      Number(custom.width) || 0,
                      custom.unit,
                      custom.dpi,
                    );
                    const curPxH = toPx(
                      Number(custom.height) || 0,
                      custom.unit,
                      custom.dpi,
                    );
                    setCustom({
                      ...custom,
                      unit: nextUnit,
                      width: fromPx(curPxW, nextUnit, custom.dpi),
                      height: fromPx(curPxH, nextUnit, custom.dpi),
                    });
                  }}
                >
                  {(["px", "in", "cm", "mm"] as SizeUnit[]).map((u) => (
                    <option key={u} value={u}>
                      {UNIT_LABEL[u]}
                    </option>
                  ))}
                </select>
              </span>

              {custom.unit !== "px" && (
                <span className="flex items-center gap-1.5">
                  <label className="flex items-center gap-1 text-xs">
                    DPI
                    <input
                      className={`min-h-[28px] w-16 rounded-[7px] border border-st-line bg-white px-1.5 py-0.5 text-xs text-st-ink ${ring}`}
                      inputMode="numeric"
                      value={custom.dpi}
                      aria-label="DPI (resolution)"
                      onChange={(event) => {
                        const dpi = Math.max(
                          1,
                          Number(event.target.value.replace(/\D/g, "")) ||
                            DEFAULT_DPI,
                        );
                        setCustom({ ...custom, dpi });
                      }}
                    />
                  </label>
                  <small className="text-xs text-st-muted">
                    ≈ {toPx(Number(custom.width) || 0, custom.unit, custom.dpi)}{" "}
                    ×{" "}
                    {toPx(Number(custom.height) || 0, custom.unit, custom.dpi)}{" "}
                    px
                  </small>
                </span>
              )}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}

// ================================================================
// VERSION HISTORY
// ================================================================

const errText = (error: unknown, fallback: string): string =>
  error instanceof Error
    ? error.message
    : typeof error === "string"
      ? error
      : fallback;

export function VersionsDrawer({
  design,
  onClose,
  onRestored,
  beforeSave,
  notify,
}: VersionsDrawerProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [list, setList] = useState<Version[] | null>(null);
  const [label, setLabel] = useState<string>("");
  const [working, setWorking] = useState<number | string | "save" | null>(null);

  const load = async () => {
    try {
      const result = await dispatch(fetchStudioVersions(design.id)).unwrap();
      setList((result || []) as Version[]);
    } catch (error: unknown) {
      notify(errText(error, "Failed to fetch versions"), "bad");
      setList([]);
    }
  };

  useEffect(() => {
    void load();

    // We intentionally only reload when the design changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.id]);

  async function saveVersion() {
    setWorking("save");

    try {
      const thumbnailDataUrl = await beforeSave();

      await dispatch(
        saveStudioVersion({
          id: design.id,
          label: label.trim(),
          thumbnailDataUrl,
        }),
      ).unwrap();

      setLabel("");
      await load();
      notify("Version saved.", "good");
    } catch (error: unknown) {
      notify(errText(error, "Failed to save version"), "bad");
    } finally {
      setWorking(null);
    }
  }

  async function restore(version: Version) {
    const versionName = version.label || "Automatic save";

    if (
      !window.confirm(
        `Restore “${versionName}” from ${when(
          version.createdAt,
        )}? What you have now is kept as a version first.`,
      )
    ) {
      return;
    }

    setWorking(version.id);

    try {
      const restoredDesign = await dispatch(
        restoreStudioVersion({
          id: design.id,
          versionId: version.id,
        }),
      ).unwrap();

      await onRestored(restoredDesign);
    } catch (error: unknown) {
      notify(errText(error, "Failed to restore version"), "bad");
    } finally {
      setWorking(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[950] grid items-stretch justify-items-end bg-[rgba(9,20,17,0.45)]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <aside
        className="flex h-full w-[min(400px,100vw)] animate-st-slide-in flex-col bg-white shadow-[-20px_0_50px_-20px_rgba(0,0,0,0.4)]"
        role="dialog"
        aria-label="Version history"
      >
        <header className={MODAL_HEAD}>
          <h2 className={`m-0 ${DISPLAY} text-xl font-bold`}>
            Version history
          </h2>

          <button className={iconBtn} onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="overflow-y-auto px-[18px] pb-6 pt-1">
          <div className={CARD_SOFT}>
            <label className="mb-2.5 grid gap-1.5 text-[13px] font-semibold">
              <span>Name this version (optional)</span>

              <input
                className={INPUT}
                value={label}
                maxLength={80}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="e.g. Sent to client"
              />
            </label>

            <button
              className={btn("accent", "md", true)}
              disabled={working === "save"}
              onClick={saveVersion}
            >
              {working === "save" ? "Saving…" : "Save this version"}
            </button>
          </div>

          <p className={FINEPRINT}>
            Studio also keeps an automatic version every 10 minutes while you
            edit (the last 40 are kept).
          </p>

          {list === null && <p className={HINT}>Loading…</p>}

          {list && list.length === 0 && (
            <p className={HINT}>No versions yet.</p>
          )}

          <ol className="m-0 mt-[14px] grid list-none gap-2 p-0">
            {(list || []).map((version) => (
              <li key={version.id} className={LIST_ROW}>
                {version.thumbnailUrl ? (
                  <img
                    className="h-16 w-16 flex-none rounded-lg bg-st-paper object-cover"
                    src={version.thumbnailUrl}
                    alt=""
                  />
                ) : (
                  <span className="grid h-16 w-16 flex-none place-items-center rounded-lg bg-st-paper text-st-muted">
                    <Icon name="history" size={18} />
                  </span>
                )}

                <span className="grid min-w-0 flex-1 gap-0.5">
                  <strong className="truncate text-sm">
                    {version.label || "Automatic save"}
                  </strong>

                  <small className="text-xs text-st-muted">
                    {when(version.createdAt)}
                    {version.auto ? " · auto" : ""}
                  </small>
                </span>

                <button
                  className={btn("plain", "sm")}
                  disabled={working !== null}
                  onClick={() => restore(version)}
                >
                  {working === version.id ? "Restoring…" : "Restore"}
                </button>
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>
  );
}

// ================================================================
// DATE FORMAT
// ================================================================

function when(value: number | string | Date): string {
  const date = value instanceof Date ? value : new Date(value);

  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ================================================================
// SHORTCUTS
// ================================================================

const KEYS: Array<[string, ...string[]]> = [
  ["Undo / redo", "Ctrl Z", "Ctrl Y"],
  ["Save now", "Ctrl S"],
  ["Copy / cut / paste (also to another page)", "Ctrl C", "Ctrl X", "Ctrl V"],
  ["Duplicate", "Ctrl D"],
  ["Select all", "Ctrl A"],
  ["Group / ungroup", "Ctrl G", "Ctrl Shift G"],
  ["Copy style / paste style", "Ctrl Alt C", "Ctrl Alt V"],
  ["Bring forward / send backward", "Ctrl ]", "Ctrl ["],
  ["To front / to back", "Ctrl Alt ]", "Ctrl Alt ["],
  ["Lock or unlock", "Ctrl Shift L"],
  ["Move 1 px / 10 px", "Arrows", "Shift Arrows"],
  ["Delete", "Delete"],
  ["Add text", "T"],
  ["Add a page", "Ctrl Enter"],
  ["Next / previous page", "Page Down", "Page Up"],
  ["Crop a photo", "Double-click"],
  ["Zoom", "Ctrl Scroll"],
  ["Stop cropping / deselect", "Esc"],
  ["This list", "?"],
];

export function ShortcutsDialog({ onClose }: ShortcutsDialogProps) {
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose} width={520}>
      <table className="w-full border-collapse text-sm">
        <tbody>
          {KEYS.map(([what, ...keys]) => (
            <tr key={what}>
              <td className="border-b border-st-line px-1 py-[7px]">{what}</td>

              <td className="whitespace-nowrap border-b border-st-line px-1 py-[7px] text-right">
                {keys.map((key) => (
                  <kbd
                    key={key}
                    className={`ml-1 inline-block rounded-md border border-b-2 border-st-line bg-st-paper px-[7px] py-[3px] ${MONO} text-xs font-semibold leading-none`}
                  >
                    {key}
                  </kbd>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className={FINEPRINT}>On a Mac use ⌘ instead of Ctrl.</p>
    </Modal>
  );
}
